/**
 * Shared type-category grouping for deck exporters.
 *
 * Used by the Text/Markdown summary exporters (zone-aware grouping) and the
 * AI Chat exporter (pure type-category grouping within the main deck).
 */
import { getTypeCategory } from '@/lib/cardUtils';
import type { DeckZone } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

/** Minimum shape a card needs to be grouped */
export interface GroupableCard {
  zone: DeckZone;
  quantity: number;
  type_line: string;
  card: { name: string };
}

export interface CardCategory<T> {
  id: string;
  title: string;
  cards: T[];
  /** Sum of quantities in this category */
  count: number;
  sortOrder: number;
}

// ============================================================================
// Category Config
// ============================================================================

/** Category titles and ordering (matches DeckSummaryView categories) */
export const SUMMARY_CATEGORY_CONFIG: Record<string, { title: string; sortOrder: number }> = {
  commander: { title: 'Commander', sortOrder: 0 },
  companion: { title: 'Companion', sortOrder: 1 },
  creature: { title: 'Creatures', sortOrder: 2 },
  planeswalker: { title: 'Planeswalkers', sortOrder: 3 },
  artifact: { title: 'Artifacts', sortOrder: 4 },
  enchantment: { title: 'Enchantments', sortOrder: 5 },
  instant: { title: 'Instants', sortOrder: 6 },
  sorcery: { title: 'Sorceries', sortOrder: 7 },
  battle: { title: 'Battles', sortOrder: 8 },
  land: { title: 'Lands', sortOrder: 9 },
  other: { title: 'Other', sortOrder: 10 },
  sideboard: { title: 'Sideboard', sortOrder: 100 },
  outsideTheGame: { title: 'Outside the Game', sortOrder: 101 },
};

// ============================================================================
// Grouping
// ============================================================================

function groupBy<T extends GroupableCard>(cards: T[], keyOf: (card: T) => string): CardCategory<T>[] {
  const groups = new Map<string, T[]>();

  for (const card of cards) {
    const key = keyOf(card);
    const existing = groups.get(key);
    if (existing) {
      existing.push(card);
    } else {
      groups.set(key, [card]);
    }
  }

  const result: CardCategory<T>[] = [];

  for (const [id, categoryCards] of groups) {
    const config = SUMMARY_CATEGORY_CONFIG[id] ?? SUMMARY_CATEGORY_CONFIG.other;
    const sortedCards = [...categoryCards].sort((a, b) => a.card.name.localeCompare(b.card.name));

    result.push({
      id,
      title: config.title,
      cards: sortedCards,
      count: sortedCards.reduce((sum, c) => sum + c.quantity, 0),
      sortOrder: config.sortOrder,
    });
  }

  result.sort((a, b) => a.sortOrder - b.sortOrder);

  return result;
}

/** Resolve a card's type category, falling back to 'other' for uncommon types */
function typeCategoryKey(typeLine: string): string {
  const category = getTypeCategory(typeLine);
  return category in SUMMARY_CATEGORY_CONFIG ? category : 'other';
}

/**
 * Group cards for the summary exporters: special zones become their own
 * category, main-deck cards are grouped by type.
 */
export function groupCardsForSummary<T extends GroupableCard>(cards: T[]): CardCategory<T>[] {
  return groupBy(cards, (card) => {
    switch (card.zone) {
      case 'commander':
      case 'companion':
      case 'sideboard':
        return card.zone;
      case 'maybeboard':
      case 'tokens':
        return 'outsideTheGame';
      default:
        return typeCategoryKey(card.type_line);
    }
  });
}

/** Group cards purely by type category, ignoring zone. */
export function groupCardsByTypeCategory<T extends GroupableCard>(cards: T[]): CardCategory<T>[] {
  return groupBy(cards, (card) => typeCategoryKey(card.type_line));
}
